import { Command } from 'commander';
import type { BrandConfig } from '../branding/types';
import { resolvePaths, resolveProfile } from '../branding/apply';
import { SpendStore, type SpendCapsState } from '../storage/spend-store';
import { createSpendTrackerObserver } from '../observers/spend-tracker';

/**
 * `<brand> caps`         - show current caps and usage
 * `<brand> caps set ...` - write new caps and (when run in-process) reload
 *
 * `caps set` writes immediately. A long-running `<brand> run` process
 * re-reads the file inside the spend-tracker's preflight mutex, so the
 * new caps take effect on the next dispatch without a restart.
 */
export function buildCapsCommand(brand: BrandConfig): Command {
  const caps = new Command('caps').description(
    `Show or update spend caps for ${brand.product.title}`,
  );

  caps
    .command('show', { isDefault: true })
    .description('Show current caps and per-period usage')
    .option('-p, --profile <name>', 'Config profile whose caps/usage to show (default "default")')
    .action((opts: { profile?: string }) => {
      const paths = resolvePaths(brand, resolveProfile(brand, opts.profile));
      const tracker = createSpendTrackerObserver(brand, paths.spendStateFile);
      const current = tracker.currentCaps();
      const usage = tracker.usage();

      console.log('Caps:');
      console.log(`  daily   tokens: ${current.dailyTokens}`);
      console.log(`  monthly tokens: ${current.monthlyTokens}`);
      if (current.dailyUsd !== undefined) {
        console.log(`  daily   usd:    ${current.dailyUsd}`);
      }
      if (current.monthlyUsd !== undefined) {
        console.log(`  monthly usd:    ${current.monthlyUsd}`);
      }
      console.log('');
      console.log('Used (rolling):');
      console.log(
        `  daily   tokens: ${usage.daily.totalTokens} (in=${usage.daily.inputTokens} out=${usage.daily.outputTokens})`,
      );
      console.log(
        `  monthly tokens: ${usage.monthly.totalTokens} (in=${usage.monthly.inputTokens} out=${usage.monthly.outputTokens})`,
      );
    });

  caps
    .command('set')
    .description('Update caps (writes immediately; live run reloads in-process)')
    .option('--daily-tokens <n>', 'Daily token cap')
    .option('--monthly-tokens <n>', 'Monthly token cap')
    .option('--daily-usd <n>', 'Daily USD cap')
    .option('--monthly-usd <n>', 'Monthly USD cap')
    .option('-p, --profile <name>', 'Config profile whose caps to update (default "default")')
    .action(async (opts: { dailyTokens?: string; monthlyTokens?: string; dailyUsd?: string; monthlyUsd?: string; profile?: string }) => {
      const paths = resolvePaths(brand, resolveProfile(brand, opts.profile));
      const store = new SpendStore(paths.spendStateFile);
      const before = store.read({
        dailyTokens: brand.defaultCaps.dailyTokens,
        monthlyTokens: brand.defaultCaps.monthlyTokens,
        dailyUsd: brand.defaultCaps.dailyUsd,
        monthlyUsd: brand.defaultCaps.monthlyUsd,
      }).caps;

      const next: SpendCapsState = {
        dailyTokens: parseOrKeep(opts.dailyTokens, before.dailyTokens),
        monthlyTokens: parseOrKeep(opts.monthlyTokens, before.monthlyTokens),
        dailyUsd: parseOrKeepOptional(opts.dailyUsd, before.dailyUsd),
        monthlyUsd: parseOrKeepOptional(opts.monthlyUsd, before.monthlyUsd),
      };

      await store.setCaps(next);

      console.log('Updated caps:');
      console.log(JSON.stringify(next, null, 2));
    });

  return caps;
}

function parseOrKeep(value: string | undefined, fallback: number): number {
  if (value === undefined) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`Invalid numeric value: ${value}`);
  }
  return n;
}

function parseOrKeepOptional(
  value: string | undefined,
  fallback: number | undefined,
): number | undefined {
  if (value === undefined) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    throw new Error(`Invalid numeric value: ${value}`);
  }
  return n;
}
